import axios from 'axios';
import { getApiUrl } from '../config/environment';

const API_URL = getApiUrl();

export interface Order {
  id: string;
  customer_id: string;
  order_number: string;
  total_amount: number;
  currency: string;
  status: 'pending' | 'processing' | 'completed' | 'cancelled' | 'refunded';
  payment_method?: string;
  payment_status: 'pending' | 'paid' | 'failed' | 'refunded';
  stripe_payment_intent_id?: string;
  stripe_subscription_id?: string;
  billing_address?: any;
  items: any;
  package_id?: string;
  domain_name?: string;
  domain_action?: 'register' | 'transfer' | 'existing';
  billing_cycle?: 'monthly' | 'yearly';
  contact_info?: any;
  created_at: string;
  updated_at: string;
}

export interface CreateOrderData {
  packageId: string;
  domainName: string;
  domainAction: 'register' | 'transfer' | 'existing';
  billingCycle: 'monthly' | 'yearly';
  contactInfo: any;
  pricingDetails: {
    hostingPrice: number;
    domainPrice: number;
    discount: number;
    total: number;
  };
  paymentMethod: 'stripe' | 'paypal';
  serviceType?: string;
  total_amount?: number;
  currency?: string;
  status?: string;
  payment_status?: string;
  billing_address?: any;
  items?: any;
}

export interface CreateDomainOrderData {
  domain_name: string;
  billing_cycle: 'yearly';
  contact_info: any;
  pricing_details: {
    domainPrice: number;
    discount: number;
    total: number;
  };
  payment_method: 'stripe' | 'paypal';
  total_amount?: number;
  currency?: string;
  status?: string;
  payment_status?: string;
  billing_address?: any;
}

export const orderService = {
  async createOrder(orderData: CreateOrderData): Promise<{ success: boolean; data?: Order; error?: string }> {
    try {
      const token = localStorage.getItem('token');
      console.log('🔧 Order Service - Token:', token ? 'Present' : 'Missing');
      console.log('🔧 Order Service - API URL:', getApiUrl('/orders'));
      console.log('🔧 Order Service - Data:', JSON.stringify(orderData, null, 2));
      
      const response = await axios.post(getApiUrl('/orders'), orderData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('🔧 Order Service - Response:', response.data);
      return { success: true, data: response.data.data };
    } catch (error: any) {
      console.error('🔧 Order Service - Error:', error);
      console.error('🔧 Order Service - Error Response:', error.response?.data);
      console.error('🔧 Order Service - Error Status:', error.response?.status);
      
      let errorMessage = 'Failed to create order';
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return {
        success: false,
        error: errorMessage,
      };
    }
  },

  async createGuestOrder(orderData: CreateOrderData & { isGuestCheckout?: boolean; customerType?: string; isExistingCustomer?: boolean }): Promise<{ success: boolean; data?: Order; error?: string }> {
    try {
      // Try guest endpoint first
      try {
        const response = await axios.post(getApiUrl('/orders/guest'), orderData);
        return { success: true, data: response.data.data };
      } catch (guestError: any) {
        // If guest endpoint doesn't exist, fall back to regular orders endpoint without auth
        console.log('Guest endpoint not available, trying regular endpoint without auth...');
        const response = await axios.post(getApiUrl('/orders'), orderData);
        return { success: true, data: response.data.data };
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to create order',
      };
    }
  },

  async getOrder(orderId: string): Promise<{ success: boolean; data?: Order; error?: string }> {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(getApiUrl(`/orders/${orderId}`), {
        headers: { Authorization: `Bearer ${token}` }
      });
      return { success: true, data: response.data.data };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch order',
      };
    }
  },

  async getMyOrders(limit = 20, offset = 0): Promise<{ success: boolean; data?: Order[]; error?: string }> {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(getApiUrl('/orders/my-orders'), {
        headers: { Authorization: `Bearer ${token}` },
        params: { limit, offset }
      });
      return { success: true, data: response.data.data };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch orders',
      };
    }
  },

  async getAllOrders(limit = 1000, offset = 0): Promise<{ success: boolean; data?: Order[]; error?: string }> {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(getApiUrl('/orders'), {
        headers: { Authorization: `Bearer ${token}` },
        params: { limit, offset }
      });
      return { success: true, data: response.data.data };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch orders',
      };
    }
  },

  async updateOrderStatus(orderId: string, updateData: {
    status?: string;
    paymentStatus?: string;
    stripePaymentIntentId?: string;
    stripeSubscriptionId?: string;
  }): Promise<{ success: boolean; data?: Order; error?: string }> {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(getApiUrl(`/orders/${orderId}/status`), updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return { success: true, data: response.data.data };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to update order',
      };
    }
  },

  async getOrderStats(): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(getApiUrl('/orders/admin/stats'), {
        headers: { Authorization: `Bearer ${token}` }
      });
      return { success: true, data: response.data.data };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch order stats',
      };
    }
  },

  async getAdminStats(): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(getApiUrl('/orders/admin/all-stats'), {
        headers: { Authorization: `Bearer ${token}` }
      });
      return { success: true, data: response.data.data };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch admin stats',
      };
    }
  },

  async createDomainOrder(orderData: CreateDomainOrderData): Promise<{ success: boolean; data?: Order; error?: string }> {
    try {
      const token = localStorage.getItem('token');
      console.log('🔧 Domain Order Service - Token:', token ? 'Present' : 'Missing');
      console.log('🔧 Domain Order Service - API URL:', getApiUrl('/orders/domain'));
      console.log('🔧 Domain Order Service - Data:', JSON.stringify(orderData, null, 2));
      
      // Transform domain order data to match backend expectations
      const transformedData = {
        packageId: 'domain-only', // Special package ID for domain-only orders
        domainName: orderData.domain_name,
        domainAction: 'register' as const,
        billingCycle: orderData.billing_cycle,
        contactInfo: orderData.contact_info,
        pricingDetails: {
          hostingPrice: 0,
          domainPrice: orderData.pricing_details.domainPrice,
          discount: orderData.pricing_details.discount,
          total: orderData.pricing_details.total,
        },
        paymentMethod: orderData.payment_method,
        totalAmount: orderData.total_amount || orderData.pricing_details.total,
        currency: orderData.currency || 'USD',
        billingAddress: orderData.billing_address || orderData.contact_info.address,
      };
      
      const response = await axios.post(getApiUrl('/orders'), transformedData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('🔧 Domain Order Service - Response:', response.data);
      return { success: true, data: response.data.data };
    } catch (error: any) {
      console.error('🔧 Domain Order Service - Error:', error);
      console.error('🔧 Domain Order Service - Error Response:', error.response?.data);
      console.error('🔧 Domain Order Service - Error Status:', error.response?.status);
      
      let errorMessage = 'Failed to create domain order';
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return {
        success: false,
        error: errorMessage,
      };
    }
  },

  async createGuestDomainOrder(orderData: CreateDomainOrderData & { isGuestCheckout?: boolean; customerType?: string; isExistingCustomer?: boolean }): Promise<{ success: boolean; data?: Order; error?: string }> {
    try {
      // Transform domain order data to match backend expectations
      const transformedData = {
        packageId: 'domain-only',
        domainName: orderData.domain_name,
        domainAction: 'register' as const,
        billingCycle: orderData.billing_cycle,
        contactInfo: orderData.contact_info,
        pricingDetails: {
          hostingPrice: 0,
          domainPrice: orderData.pricing_details.domainPrice,
          discount: orderData.pricing_details.discount,
          total: orderData.pricing_details.total,
        },
        paymentMethod: orderData.payment_method,
        totalAmount: orderData.total_amount || orderData.pricing_details.total,
        currency: orderData.currency || 'USD',
        billingAddress: orderData.billing_address || orderData.contact_info.address,
        isGuestCheckout: orderData.isGuestCheckout,
        customerType: orderData.customerType,
        isExistingCustomer: orderData.isExistingCustomer,
      };

      // Try guest endpoint first (without auth headers)
      try {
        const response = await axios.post(getApiUrl('/orders/guest'), transformedData, {
          headers: {
            'Content-Type': 'application/json'
            // Explicitly not sending Authorization header
          }
        });
        return { success: true, data: response.data.data };
      } catch (guestError: any) {
        console.log('Guest endpoint not available, error:', guestError.response?.status, guestError.response?.data);
        
        // If guest endpoint doesn't exist, fall back to regular orders endpoint without auth
        console.log('Trying regular endpoint without auth headers...');
        
        const response = await axios.post(getApiUrl('/orders'), transformedData, {
          headers: {
            'Content-Type': 'application/json'
            // Explicitly not sending Authorization header
          }
        });
        return { success: true, data: response.data.data };
      }
    } catch (error: any) {
      let errorMessage = 'Failed to create domain order';
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return {
        success: false,
        error: errorMessage,
      };
    }
  },

  async syncStripeOrdersAdmin(): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(getApiUrl('/orders/admin/sync-stripe'), {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return { success: true, data: response.data.data };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to sync Stripe orders',
      };
    }
  }
};